<template>
  <div :id="'list' + id" class="to-do-list-container d-flex flex-column" ref="listContainer" :class="{
    'old-date': !customTodoList && moments(id).isBefore(Date(), 'day'),
    'to-do-list-container--mobile': mobileLayout,
  }" :style="`flex: 0 0 ${100 / columns}%;`">
    <div v-if="loading" class="loading-spinner">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <list-header
      :id="id"
      :customTodoList="customTodoList"
      :cTodoListIndex="cTodoListIndex"
      :toDoList="toDoListState"
      :inlineDetailsOpen="inlineDetailsOpen"
      @toggle-inline-details="inlineDetailsOpen = !inlineDetailsOpen"
    >
    </list-header>
    <!-- 手机：新任务在列表顶部；桌面仍在底部（记事本式空白区） -->
    <div v-if="mobileLayout" class="mobile-new-todo-bar">
      <div class="todo-item-container mobile-new-todo-row">
        <input
          class="todo-input new-todo-input"
          type="text"
          ref="newToDoInput"
          v-model="newToDo.text"
          :placeholder="$t('ui.newTask')"
          enterkeyhint="done"
          @blur="addToDo()"
          @keyup.enter="onNewTodoEnter"
          @keyup.esc="cancelAdd()"
        />
      </div>
    </div>
    <ul class="to-do-list">
      <li v-for="(toDo, index) in toDoListState" :key="index">
        <div class="drop-zone" @drop="onDrop($event, id, index)" @dragover.prevent @dragenter.prevent>
          <mobile-to-do-item
            v-if="mobileLayout"
            :to-do="toDo"
            :index="index"
            :to-do-list-id="id"
            :show-inline-details="inlineDetailsOpen"
          ></mobile-to-do-item>
          <to-do-item
            v-else
            :to-do="toDo"
            :index="index"
            :to-do-list-id="id"
          ></to-do-item>
        </div>
      </li>
    </ul>
    <div
      class="fake-drop-zone margin-bottom-2"
      :class="{
        'flex-grow-1': !mobileLayout,
        'mobile-list-end-drop': mobileLayout,
        'fake-drag-hover': fakeItemsDragHover,
      }"
      @drop="onDropAtEnd($event, id)"
      @dragenter.self="onDragenter"
      @dragleave.self="onDragleave"
      @dragover.prevent
    >
      <template v-if="!mobileLayout">
        <div class="todo-item-container">
          <input
            class="todo-input new-todo-input"
            type="text"
            ref="newToDoInput"
            v-model="newToDo.text"
            enterkeyhint="done"
            @blur="addToDo()"
            @keyup.enter="onNewTodoEnter"
            @keyup.esc="cancelAdd()"
          />
        </div>
        <div class="fake-lines" :class="{ 'custom-list': customTodoList }" @click="$refs.newToDoInput.focus()"></div>
      </template>
    </div>
  </div>
</template>

<script>
import toDoItem from "./toDoItem";
import MobileToDoItem from "../mobile/MobileToDoItem.vue";
import moment from "moment";
import toDoListRepository from "../repositories/toDoListRepository";
import listHeader from "./listHeader";
import notifications from "../helpers/notifications";
import repeatingEventHelper from "../helpers/repeatingEvents.js";
import tasksHelper from "../helpers/tasksHelper";

function afterTodoWrite(promise, fn) {
  Promise.resolve(promise).then((result) => {
    let ok = false;
    try {
      ok = require("../helpers/todoActivityLog.js").isTodoWriteOk(result);
    } catch (_) {
      ok = false;
    }
    if (!ok) return;
    fn();
  }, () => {});
}

function listActivityLabel(vm, listId) {
  const id = listId == null ? "" : String(listId);
  if (/^\d{8}$/.test(id)) return moment(id, "YYYYMMDD").format("YYYY-MM-DD");
  const rows = (vm.$store && vm.$store.getters && vm.$store.getters.cTodoListIds) || [];
  for (let i = 0; i < rows.length; i++) {
    if (rows[i] && String(rows[i].listId) === id && rows[i].listName) return rows[i].listName;
  }
  return id;
}

function logMovedAcrossLists(vm, toDo, fromListId, toListId, originWrite, destWrite) {
  if (String(fromListId) === String(toListId)) return;
  const meta = {
    fromLabel: listActivityLabel(vm, fromListId),
    toLabel: listActivityLabel(vm, toListId),
  };
  afterTodoWrite(Promise.all([originWrite, destWrite]), () => {
    logActivity(vm, "moved", { id: toDo && toDo.id, text: toDo && toDo.text }, toListId, meta);
  });
}

function logActivity(vm, type, todo, listId, meta) {
  try {
    const { logTodoActivity } = require("../helpers/todoActivityLog.js");
    const { createNote } = require("../sync/engine");
    const pending = logTodoActivity({
      type,
      todo,
      listId,
      meta,
      t: vm.$t.bind(vm),
      createNoteFn: createNote,
    });
    if (pending && typeof pending.then === "function") pending.then(() => {}, () => {});
  } catch (err) {
    /* activity log must not block the todo write */
  }
}

export default {
  components: {
    listHeader,
    toDoItem,
    MobileToDoItem,
  },
  props: {
    id: { required: false, type: String },
    customTodoList: { required: false, default: false, type: Boolean },
    cTodoListIndex: { required: false, type: Number },
    showCustomList: { required: false, type: Boolean },
    mobileLayout: { type: Boolean, default: false },
  },
  data() {
    return {
      newToDo: { text: "", checked: false },
      fakeItemCounts: 6,
      fakeItemsDragHover: false,
      loading: false,
      inlineDetailsOpen: false,
    };
  },
  mounted() {
    this.setTodoListHeight();
    window.addEventListener("resize", this.setTodoListHeight);
    let listId = this.id;
    this.loading = true;
    const finish = () => {
      this.$store.dispatch("loadRepeatingEventGeneratedByDate", listId).then(() => {
        this.loading = false;
        repeatingEventHelper.generateRepeatingEventsIntances(listId, this);
      });
      this.clearRemovedRepeatingEvents();
      this.$emit("todoListMounted", listId);
    };
    // Calendar days are owned by week sync (server authority). Skipping IDB here
    // avoids a late IndexedDB read stomping a fresher applyWeekToStore result.
    if (this.isCalendarDay) {
      const { getWeekCache } = require("../sync/sessionSync.js");
      const cached = getWeekCache().getDay(listId);
      if (cached && Array.isArray(cached.payload) && getWeekCache().getRevision(listId) !== undefined) {
        this.$store.commit("loadTodoLists", { todoListId: listId, todoList: cached.payload });
      }
      finish();
      return;
    }
    this.$store.dispatch("loadTodoLists", listId).then(finish);
  },
  unmounted() {
    window.removeEventListener("resize", this.setTodoListHeight);
  },
  beforeCreate() {
    let listId = this.id;
    // Do not wipe an already-synced list on remount — week sync / cache may already have it
    if (this.$store.getters.todoLists[listId] === undefined) {
      this.$store.commit("loadTodoLists", { todoListId: listId, todoList: [] });
    }
  },
  methods: {
    addToDo: function () {
      if (this.newToDo.text != "") {
        const { newTodoId, nowIso } = require("../sync/todoMerge.js");
        var newTodo = {
          id: newTodoId(),
          updatedAt: nowIso(),
          text: this.newToDo.text,
          checked: false,
          listId: this.id,
          desc: "",
          subTaskList: [],
          color: "none",
          priority: 0,
          tags: [],
          time: null,
          alarm: false,
          repeatingEvent: null,
        };
        this.$store.commit("addTodo", newTodo);
        const listId = this.id;
        const list = this.$store.getters.todoLists[listId];
        tasksHelper.applyListOrder(list, this.$store.getters.config);
        const written = this.updateTodoList(listId, list);
        this.newToDo.text = "";
        this.$nextTick(() => this.scrollToTodo(newTodo));
        afterTodoWrite(written, () => {
          logActivity(this, "created", newTodo, listId);
        });
      }
    },
    scrollToTodo: function (todo) {
      const root = this.$refs.listContainer;
      if (!root) return;
      const todos = this.$store.getters.todoLists[this.id];
      const idx = todos && todo ? todos.indexOf(todo) : -1;
      const items = root.querySelectorAll(".to-do-list > li");
      const target = idx >= 0 && items[idx] ? items[idx] : items[0];
      if (target && typeof target.scrollIntoView === "function") {
        try {
          target.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
        } catch (e) {
          target.scrollIntoView(false);
        }
      }
      if (this.mobileLayout && idx === 0) {
        root.scrollTop = 0;
      }
    },
    onNewTodoEnter: function (event) {
      if (event && (event.isComposing || event.keyCode === 229)) return;
      this.addToDo();
      if (this.mobileLayout) {
        this.$nextTick(() => {
          const input = this.$refs.newToDoInput;
          if (input) input.focus();
        });
      }
    },
    cancelAdd: function () {
      this.newToDo.text = "";
    },
    moments: function (date) {
      return moment(date);
    },
    onDrop: function (event, list, new_index) {
      let toDo = JSON.parse(event.dataTransfer.getData("item"));
      let index = event.dataTransfer.getData("index");
      const fromListId = toDo.listId;
      this.$store.commit("removeTodo", {
        toDoListId: toDo.listId,
        index: index,
      });
      const originWrite = this.updateTodoList(toDo.listId, this.$store.getters.todoLists[toDo.listId]);
      if (toDo.listId != list) toDo.repeatingEvent = null;
      toDo.listId = list;
      this.$store.commit("insertTodo", {
        toDoListId: list,
        index: new_index,
        toDo: toDo,
      });
      const dropped = this.$store.getters.todoLists[list];
      tasksHelper.applyListOrder(dropped, this.$store.getters.config);
      const destWrite = this.updateTodoList(list, dropped);
      logMovedAcrossLists(this, toDo, fromListId, list, originWrite, destWrite);
    },
    onDropAtEnd: function (event, list) {
      let toDo = JSON.parse(event.dataTransfer.getData("item"));
      let index = event.dataTransfer.getData("index");
      const fromListId = toDo.listId;
      this.$store.commit("removeTodo", { toDoListId: toDo.listId, index: index, });
      const originWrite = this.updateTodoList(toDo.listId, this.$store.getters.todoLists[toDo.listId]);
      if (toDo.listId != list) toDo.repeatingEvent = null;
      toDo.listId = list;
      const nextList = this.$store.getters.todoLists[list];
      const moveCompleted = tasksHelper.shouldMoveCompletedToBottom(this.$store.getters.config);
      tasksHelper.appendPreservingSections(nextList, toDo, moveCompleted);
      tasksHelper.applyListOrder(nextList, this.$store.getters.config);
      const destWrite = this.updateTodoList(list, nextList);
      logMovedAcrossLists(this, toDo, fromListId, list, originWrite, destWrite);
      this.fakeItemsDragHover = false;
    },
    updateTodoList: function (todoListId, TodoList) {
      notifications.refreshDayNotifications(this, todoListId);
      return toDoListRepository.update(todoListId, TodoList);
    },
    setTodoListHeight: function () {
      if (this.showCustomList) {
        this.fakeItemCounts = Math.floor(this.$refs.listContainer.clientHeight / 40);
      } else {
        this.fakeItemCounts = Math.floor(this.$refs.listContainer.clientHeight / 34);
      }
    },
    onDragenter: function () {
      this.fakeItemsDragHover = true;
    },
    onDragleave: function () {
      this.fakeItemsDragHover = false;
    },
    clearRemovedRepeatingEvents: function () {
      if (this.customTodoList) return;
      repeatingEventHelper.removeGeneratedRepeatingEvents(this.id, this);
    },
  },
  watch: {
    showCustomList: function () {
      this.$nextTick(function () {
        this.setTodoListHeight();
      });
    },
    // Re-materialize when a new rule lands in the date cache after this day already mounted
    // (race: create repeating while week columns are still loading).
    repeatingIdsForDay(next, prev) {
      if (this.customTodoList || this.loading) return;
      if (!next || next === prev) return;
      repeatingEventHelper.generateRepeatingEventsIntances(this.id, this);
    },
  },
  computed: {
    toDoListState: function () {
      return this.$store.getters.todoLists[this.id];
    },
    isCalendarDay() {
      return !this.customTodoList && /^\d{8}$/.test(String(this.id));
    },
    repeatingIdsForDay() {
      const bucket = this.$store.getters.repeatingEventDateCache[this.id] || [];
      return bucket.slice().sort().join(",");
    },
    columns: function () {
      if (this.customTodoList)
        return this.$store.getters.config.customColumns;
        
      return this.$store.getters.config.columns;
    },
  },
};
</script>

<style scoped>
.to-do-list {
  list-style: none;
  padding-inline-start: 0px;
  margin-bottom: 0px;
}

.to-do-list li {
  -webkit-transition: all 0.4s ease-out;
  transition: all 0.4s ease-out;
}

.todo-input {
  height: 1.28rem;
  line-height: 1.3rem;
  font-size: 0.865rem;
  margin: 2px 0px 2px 0px;
  padding: 0 6px 0 6px;
}

.to-do-list-container {
  padding-left: 13px;
  padding-right: 13px;
  scroll-snap-align: start;
  margin-bottom: 5px;
}

.to-do-fake-item {
  height: 1.2rem;
  width: 100%;
}

.weekly-to-do-header h4 {
  margin-bottom: 4px;
}

.weekly-to-do-header span {
  margin-top: 0px;
}

.weekly-to-do-header i {
  color: grey;
}

.weekly-to-do-header i:hover {
  color: black;
}
.weekly-to-do-header i {
  font-size: 1.4rem;
  flex-grow: 0;
  align-self: start;
  cursor: pointer;
}

.fake-item-container {
  border-bottom: 1px solid #eaecef;
  height: 1.6rem;
}

.dark-theme .fake-item-container {
  border-bottom: 1px solid #30363d;
}

.fake-drop-zone * {
  pointer-events: none;
}

.fake-drop-zone:hover * {
  pointer-events: unset;
}

/* 手机：输入区始终可点，避免依赖 :hover 的二次点击 */
.to-do-list-container--mobile .fake-drop-zone,
.to-do-list-container--mobile .fake-drop-zone * {
  pointer-events: auto;
}

.to-do-list-container--mobile {
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.to-do-list-container--mobile :deep(.weekly-to-do-header),
.to-do-list-container--mobile .weekly-to-do-header {
  flex-shrink: 0;
}

/*
 * 手机：顶栏（标题+新任务）固定，仅任务列表滚动。
 * 勿再用 sticky——任务行带 transform，会与 sticky 叠层穿模。
 */
.to-do-list-container--mobile {
  overflow: hidden;
}

.to-do-list-container--mobile .to-do-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.to-do-list-container--mobile .mobile-new-todo-bar {
  flex: 0 0 auto;
  margin-bottom: 0;
  padding-bottom: 2px;
  border-bottom: 1px solid var(--m-hairline, rgba(60, 60, 67, 0.12));
  background: var(--m-surface, #fff);
  position: relative;
  z-index: 2;
  isolation: isolate;
}

.to-do-list-container--mobile .mobile-new-todo-row {
  border-bottom: none;
  min-height: 44px;
  display: flex;
  align-items: center;
  padding: 2px 2px 4px;
}

.to-do-list-container--mobile .mobile-new-todo-row .new-todo-input {
  height: auto;
  min-height: 36px;
  line-height: 1.35;
  padding: 8px 4px;
  font-size: 16px;
}

.to-do-list-container--mobile .mobile-list-end-drop {
  flex: 0 0 auto;
  min-height: 24px;
  margin-bottom: 0;
}

.fake-drag-hover .todo-item-container {
  box-shadow: rgb(244, 243, 243) 0px 0px 4px 1px inset;
  background-color: rgb(250, 249, 249);
}

.dark-theme .fake-drag-hover .todo-item-container {
  box-shadow: #0b0d12 0px 0px 4px 1px inset;
  background-color: #0c0d14;
}

.fake-drag-hover input,
.dark-theme .fake-drag-hover input {
  background-color: unset;
}

.fake-lines {
  background-image: linear-gradient(0deg,
      #ffffff 48.08%,
      #eaecef 48.08%,
      #eaecef 50%,
      #ffffff 50%,
      #ffffff 98.08%,
      #eaecef 98.08%,
      #eaecef 100%);
  background-size: 52px 52px;
  height: calc(100% - 23px);
}

.dark-theme .fake-lines {
  background-image: linear-gradient(0deg,
      #13171d 48.08%,
      #30363d 48.08%,
      #30363d 50%,
      #13171d 50%,
      #13171d 98.08%,
      #30363d 98.08%,
      #30363d 100%);
  background-size: 52px 52px;
}

.fake-lines.custom-list {
  height: calc(100% - 24px);
}

.todo-input {
  line-height: 1.3rem;
  width: 100%;
  border: none;
  font-size: 0.865rem;
}

.todo-input:focus {
  outline: none;
}

.loading-spinner {
  position: relative;
  margin: auto;
  top: 200px;
  height: 0px;
}

.todo-item-container {
  border-bottom: 1px solid #eaecef;
  height: 26px;
  z-index: 1;
  margin-bottom: -1px;
}
</style>
